/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { countryCodes } from '@/constants/locale';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { getTimeAgo } from '@/utils/date/timeAgo';
import { useCallback } from 'react';

const labelMap = {
  today: 'label_reviews_today',
  dayAgo: 'label_reviews_dayAgo',
  daysAgo: 'label_reviews_daysAgo',
  monthAgo: 'label_reviews_monthAgo',
  monthsAgo: 'label_reviews_monthsAgo',
  yearAgo: 'label_reviews_yearAgo',
  yearsAgo: 'label_reviews_yearsAgo',
  prefix: 'label_reviews_datePrefix',
};

export const useTimeAgo = () => {
  const labels = useLabels(labelMap);
  const locale = useLocale();
  const isUS = locale === countryCodes.us;

  const getPostedDate = useCallback(
    (reviewDate: number | string | Date) => {
      const timeAgo = getTimeAgo({ value: reviewDate });
      if (timeAgo.time === 0 && timeAgo.unit === 'days') {
        return labels.today;
      }
      if (timeAgo.time === 1 && timeAgo.unit === 'days') {
        return `${timeAgo.time} ${labels.dayAgo}`;
      }
      if (timeAgo.time > 1 && timeAgo.unit === 'days') {
        return `${timeAgo.time} ${labels.daysAgo}`;
      }

      if (timeAgo.time === 1 && timeAgo.unit === 'months') {
        return `${timeAgo.time} ${labels.monthAgo}`;
      }
      if (timeAgo.time > 1 && timeAgo.unit === 'months') {
        return `${timeAgo.time} ${labels.monthsAgo}`;
      }

      if (timeAgo.time === 1 && timeAgo.unit === 'years') {
        return `${timeAgo.time} ${labels.yearAgo}`;
      }
      if (timeAgo.time > 1 && timeAgo.unit === 'years') {
        return `${timeAgo.time} ${labels.yearsAgo}`;
      }
    },
    [labels],
  );

  const getLocalizedDate = useCallback(
    (reviewDate: number | string | Date) => {
      const postedDate = getPostedDate(reviewDate);
      if (isUS || postedDate === labels.today) {
        return postedDate;
      }
      return `${labels.prefix} ${postedDate}`;
    },
    [getPostedDate, isUS, labels.prefix, labels.today],
  );

  return {
    getLocalizedDate,
  };
};
